import type {
  GenerateEmergencyCodeResponse,
  ValidateEmergencyResponse,
  EmergencyTokenStatus,
  EmergencyAuditResponse,
  AdminEmergencyStatus,
  EmergencyCategory,
} from '../types/emergency';

// API Gateway base URL — update after Lambda deployment
const API_BASE = process.env.REACT_APP_EMERGENCY_API_URL || 'https://d51lig7hgh.execute-api.ca-central-1.amazonaws.com/emergency';

async function getAuthToken(): Promise<string> {
  const { fetchAuthSession } = await import('aws-amplify/auth');
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new Error('Not authenticated');
  return token;
}

async function authFetch(path: string, options: RequestInit = {}): Promise<Response> {
  const token = await getAuthToken();
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `Request failed: ${response.status}`);
  }
  return response;
}

// --- Patient endpoints (authenticated) ---

export async function generateEmergencyCode(sharedCategories?: EmergencyCategory[]): Promise<GenerateEmergencyCodeResponse> {
  const response = await authFetch('/generate', {
    method: 'POST',
    body: sharedCategories ? JSON.stringify({ sharedCategories }) : undefined,
  });
  return response.json();
}

export async function getEmergencyStatus(): Promise<EmergencyTokenStatus> {
  const response = await authFetch('/status');
  return response.json();
}

export async function revokeEmergencyCode(): Promise<void> {
  await authFetch('/revoke', { method: 'POST' });
}

export async function getEmergencyAuditLog(): Promise<EmergencyAuditResponse> {
  const response = await authFetch('/audit');
  return response.json();
}

// --- Public endpoints (no auth) ---

export async function validateEmergencyToken(token: string): Promise<ValidateEmergencyResponse> {
  const response = await fetch(`${API_BASE}/validate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  return response.json();
}

export async function validateEmergencyPin(accessId: string, pin: string): Promise<ValidateEmergencyResponse> {
  const response = await fetch(`${API_BASE}/validate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ accessId, pin }),
  });
  return response.json();
}

// --- Admin endpoints (authenticated, admin group) ---

export async function adminGetEmergencyStatus(username: string): Promise<EmergencyTokenStatus> {
  const response = await authFetch(`/admin/status?username=${encodeURIComponent(username)}`);
  return response.json();
}

export async function adminGenerateEmergencyCode(username: string, sharedCategories?: EmergencyCategory[]): Promise<GenerateEmergencyCodeResponse> {
  const response = await authFetch('/admin/generate', {
    method: 'POST',
    body: JSON.stringify({ username, ...(sharedCategories && { sharedCategories }) }),
  });
  return response.json();
}

export async function adminRevokeEmergencyCode(username: string): Promise<void> {
  await authFetch('/admin/revoke', {
    method: 'POST',
    body: JSON.stringify({ username }),
  });
}

export async function adminGetAuditLog(username: string): Promise<EmergencyAuditResponse> {
  const response = await authFetch(`/admin/audit?username=${encodeURIComponent(username)}`);
  return response.json();
}
